import Reveal from "@/components/Reveal";
import {
  TICKETS_URL,
  TICKET_TIERS,
  TICKET_DATE,
  VENUE_URL,
  VENUE_NAME,
} from "@/lib/tickets";

/**
 * Tickets section — appears on the Chapter II page.
 * Visually mirrors the four Wix ticket tiers; every CTA opens the
 * official Wix checkout in a new tab so payment + capacity stays with
 * the existing event system.
 */
export default function Tickets() {
  return (
    <section
      id="tickets"
      data-testid="chapter-ii-tickets"
      className="bone-section py-32 md:py-48"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal slow>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end mb-20 md:mb-24">
            <div className="md:col-span-7">
              <p className="hairline opacity-60 mb-6">— Attend</p>
              <h2 className="font-serif italic text-4xl md:text-6xl leading-[1.05]">
                One day. One room. One last look at Chapter 2.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-serif italic text-xl md:text-2xl opacity-80 leading-[1.4]">
                {TICKET_DATE}.&nbsp;
                <a
                  href={VENUE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="link-quiet"
                  data-testid="tickets-venue-link"
                >
                  {VENUE_NAME}, London
                </a>
                .
              </p>
              <p className="font-serif italic text-base md:text-lg opacity-60 mt-4 leading-relaxed">
                Tickets are released and processed through the official Nick
                Fraser event page. Capacity is limited.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {TICKET_TIERS.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 80} slow>
              <a
                href={TICKETS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ticket-tier-${tier.id}`}
                className={`group block h-full p-8 md:p-12 transition-colors duration-500 ${
                  tier.featured
                    ? "bg-[color:var(--ink)] text-[color:var(--bone)] hover:bg-[#1a1a1a]"
                    : "bg-[color:var(--bone-deep)] text-[color:var(--ink)] hover:bg-[#ece6de]"
                }`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="hairline opacity-60 mb-3">{tier.label}</p>
                    <p className="font-serif italic text-3xl md:text-4xl leading-tight">
                      {tier.time}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-serif italic text-3xl md:text-4xl">
                      {tier.price}
                    </p>
                    <p className="hairline opacity-55 mt-1">{tier.fee}</p>
                  </div>
                </div>

                <p className="font-serif italic text-base md:text-lg opacity-80 mt-8 max-w-md leading-relaxed">
                  {tier.description}
                </p>

                <p className="hairline mt-10 opacity-80 group-hover:opacity-100 transition-opacity">
                  Reserve via official page →
                </p>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal slow>
          <div className="mt-20 md:mt-24 flex justify-end">
            <a
              href={TICKETS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tickets-primary-cta"
              className="link-quiet font-serif italic text-xl md:text-2xl"
            >
              All ticket options →
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
